import { Component, inject, Injector, Type } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { lastValueFrom } from "rxjs";
import { AuthService, AuthUser } from "./auth.service";
import { DbQueryService } from "./db-query.service";
import { DbSaveService } from "./db-save.service";
import { DialogWrapperService } from "./dialog-wrapper.service";
import { DomainService } from "./domain.service";
import { IDialogParent } from "./i-dialog-parent.";
import { UnitOfWork } from "./unit-of-work";
import { UtilFns } from "./util-fns";


export interface DialogParams   {
  header?: string;
  closable?: boolean;
  draggable?: boolean;
  dialogWidth?: string;
  dialogHeight?: string;
}

@Component({
    selector: 'base-dialog',
    template: '',
    providers: [DialogWrapperService],
    standalone: false
})
export abstract class DialogBaseComponent<TParams extends DialogParams>  {

  public params: TParams;
  public uow: UnitOfWork;
  
  public dialogService: DialogWrapperService;  // wraps the PrimeNg dialogService
  public authUser: AuthUser;

  public dialogKey: string;

  protected dbQueryService: DbQueryService;
  protected dbSaveService: DbSaveService;
  protected authService: AuthService;
  protected toastr: ToastrService;

  private injector = inject(Injector);

  self: DialogBaseComponent<TParams>;
  public busyPromise: Promise<any> | undefined;
  

  isPageReady = false;

  constructor(
      public config: DynamicDialogConfig<TParams>,
      public dialogRef: DynamicDialogRef,
      public domainService: DomainService,

    ) {
    // We are doing this so that each dialog has its own dialogWrapperService with 'isChild' set to true
    // all child dialogs have the key = 'child' whereas the parent's have no key
    //  so any call to the confirmationService from a dialog will only see the child <p-confirmDialog>

    const injector = Injector.create({
      providers: [{ provide: DialogWrapperService, useClass: DialogWrapperService }], parent: this.injector
    });
    this.dialogService = injector.get(DialogWrapperService);
    this.dialogKey = this.dialogService.localKey.toString();

    // This doesn't seem to create a new instance of the service for each dialog.
    // this.dialogWrapperService = inject(DialogWrapperService);

    // DONT use this - we want each dialog to have its own.
    // this.dialogWrapperService = domainService.dialogWrapperService;

    // --------------------------------------------------------------------------

    this.authService = domainService.authService;
    this.authUser = domainService.authService.getUser();

    this.uow = domainService.uow;
    this.dbQueryService = domainService.dbQueryService;
    this.dbSaveService = domainService.dbSaveService;
    this.toastr = domainService.toastService;

    

    this.self = this;

    this.params = this.config.data || {} as TParams;
    this.isPageReady = false;
  }

  static async open<TParams extends DialogParams >(
    componentType: Type<DialogBaseComponent<TParams>>,
    parent: IDialogParent,
    data?: TParams
  ) {
    const fullConfig = {
        closable: false,
        draggable: true,
        resizeable: false,
        modal: true,
        width: data?.dialogWidth ?? 'calc(60vw)',
        height: data?.dialogHeight ?? 'calc(60vh)',

        // prevent maximum call stack exceeded - see https://github.com/primefaces/primeng/issues/15894
        focusOnShow: false,

        ...data,
        data: data,
      }
    const png = parent.pngDialogService ?? parent.getPngDialogService();
    const ref = png.open(componentType, fullConfig);
    // PrimeNG returns null when this dialog is already open (a double-click). Treat it as a cancel.
    if (ref == null) return null;

    const r = await lastValueFrom(ref.onClose);
    return r;
  }
 

  public isBusy() {
    return this.busyPromise != null || this.dbSaveService.isBusy();
  }

  public async wrapBusy<T>(op: Promise<T> | (() => Promise<T>)) {

    if (this.busyPromise) {
      return await (typeof op === 'function' ? op() : op);
    }

    try {
      this.busyPromise = typeof op === 'function' ? op() : op;
      return await this.busyPromise;
    } finally {
      this.busyPromise = undefined;
    }
  }

}
